import { useState, useEffect, useMemo, useRef } from 'react';
import { motion } from 'framer-motion';
import { FileText, Upload, Download, Pencil, Trash2, X, File, FileSpreadsheet, Filter, FileCheck, AlertTriangle, ChevronDown, ChevronUp, Eye, Share2 } from '../../icons/wp-icons';
import { dokumentApi } from '../../api/dokumentApi';
import { rechnungApi } from '../../api/rechnungApi';
import { mahnungApi } from '../../api/mahnungApi';

const KATEGORIEN = ['Rechnung', 'Kostenvoranschlag', 'Angebot', 'Vertrag', 'Sonstiges'];

// Dateitype Icons und Farben
const getFileIcon = (dateityp) => {
  if (dateityp?.includes('pdf')) {
    return { icon: FileText, color: 'text-red-500', bg: 'bg-red-50' };
  }
  if (dateityp?.includes('word') || dateityp?.includes('document')) {
    return { icon: FileText, color: 'text-blue-500', bg: 'bg-blue-50' };
  }
  if (dateityp?.includes('excel') || dateityp?.includes('spreadsheet')) {
    return { icon: FileSpreadsheet, color: 'text-green-500', bg: 'bg-green-50' };
  }
  if (dateityp?.includes('image')) {
    return { icon: File, color: 'text-purple-500', bg: 'bg-purple-50' };
  }
  return { icon: File, color: 'text-gray-500', bg: 'bg-gray-50' };
};

// Dateigröße formatieren
const formatFileSize = (bytes) => {
  if (bytes < 1024) return bytes + ' B';
  if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB';
  return (bytes / (1024 * 1024)).toFixed(1) + ' MB';
};

// Kategorie Badge Farben
const getKategorieBadge = (kategorie) => {
  switch (kategorie) {
    case 'Rechnung':
      return 'bg-green-100 text-green-700';
    case 'Kostenvoranschlag':
      return 'bg-orange-100 text-orange-700';
    case 'Angebot':
      return 'bg-blue-100 text-blue-700';
    case 'Vertrag':
      return 'bg-purple-100 text-purple-700';
    default:
      return 'bg-gray-100 text-gray-700';
  }
};

function DokumenteTab({ kundeId, anfragen = [], projekte = [], onDokumenteChange, readOnly = false, ansprechpartner = [] }) {
  const [dokumente, setDokumente] = useState([]);
  const [rechnungen, setRechnungen] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [editingDokument, setEditingDokument] = useState(null);
  const [editForm, setEditForm] = useState({
    kategorie: 'Sonstiges',
    anfrageId: '',
    projektId: '',
    beschreibung: ''
  });
  const [filterKategorie, setFilterKategorie] = useState('');
  const [filterAnfrage, setFilterAnfrage] = useState('');
  const [filterProjekt, setFilterProjekt] = useState('');
  const [filterAnsprechpartner, setFilterAnsprechpartner] = useState('');

  // Mahnhistorie State
  const [expandedMahnungen, setExpandedMahnungen] = useState({});
  const [mahnhistorie, setMahnhistorie] = useState({});

  // Dokument-Preview State
  const [previewDokument, setPreviewDokument] = useState(null);
  const [previewPosition, setPreviewPosition] = useState({ top: 0, left: 0 });
  const [previewUrl, setPreviewUrl] = useState(null);
  const [previewBlob, setPreviewBlob] = useState(null);
  const previewTimeout = useRef(null);

  // Mobile Detection
  const [isMobile, setIsMobile] = useState(false);

  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth <= 768 || 'ontouchstart' in window);
    };
    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  // API URL für Vorschau
  const apiUrl = import.meta.env.VITE_API_URL || 'http://localhost:3000/api';

  const handleDocMouseEnter = async (e, dok) => {
    if (previewTimeout.current) {
      clearTimeout(previewTimeout.current);
    }

    const mouseX = e.clientX;
    const mouseY = e.clientY;

    previewTimeout.current = setTimeout(async () => {
      // Position berechnen
      const popupWidth = 420;
      const isPdfDoc = dok.dateityp?.includes('pdf');
      const isImage = dok.dateityp?.includes('image');
      const popupHeight = isPdfDoc ? 520 : (isImage ? 350 : 200);

      let left = mouseX + 15;
      let top = mouseY + 15;

      if (left + popupWidth > window.innerWidth - 10) {
        left = mouseX - popupWidth - 15;
      }
      if (top + popupHeight > window.innerHeight - 10) {
        top = mouseY - popupHeight - 15;
      }

      setPreviewPosition({ top, left });
      setPreviewDokument(dok);

      // Für Bilder und PDFs: URL laden
      if (dok.dateityp?.includes('image') || dok.dateityp?.includes('pdf')) {
        try {
          const token = localStorage.getItem('token');
          const response = await fetch(`${apiUrl}/dokumente/download/${dok.id}`, {
            headers: { 'Authorization': `Bearer ${token}` }
          });
          if (response.ok) {
            const blob = await response.blob();
            const url = window.URL.createObjectURL(blob);
            setPreviewBlob(blob);
            setPreviewUrl(url);
          }
        } catch (error) {
          console.error('Preview Fehler:', error);
        }
      }
    }, 1000);
  };

  const handleDocMouseLeave = () => {
    if (previewTimeout.current) {
      clearTimeout(previewTimeout.current);
    }
    previewTimeout.current = setTimeout(() => {
      setPreviewDokument(null);
      setPreviewBlob(null);
      if (previewUrl) {
        window.URL.revokeObjectURL(previewUrl);
        setPreviewUrl(null);
      }
    }, 300);
  };

  const handlePreviewMouseEnter = () => {
    if (previewTimeout.current) {
      clearTimeout(previewTimeout.current);
    }
  };

  const handlePreviewMouseLeave = () => {
    setPreviewDokument(null);
    setPreviewBlob(null);
    if (previewUrl) {
      window.URL.revokeObjectURL(previewUrl);
      setPreviewUrl(null);
    }
  };

  // Close preview (für Mobile und Desktop)
  const closePreview = () => {
    if (previewTimeout.current) {
      clearTimeout(previewTimeout.current);
    }
    setPreviewDokument(null);
    setPreviewBlob(null);
    if (previewUrl) {
      window.URL.revokeObjectURL(previewUrl);
      setPreviewUrl(null);
    }
  };

  // Mobile Touch Handler - öffnet Preview sofort bei Tap
  const handleDocClick = async (e, dok) => {
    if (!isMobile) return; // Nur auf Mobile

    e.preventDefault();
    e.stopPropagation();

    // Wenn schon ein Preview offen ist, schließen
    if (previewDokument?.id === dok.id) {
      closePreview();
      return;
    }

    setPreviewDokument(dok);

    // Für Bilder und PDFs: URL laden
    if (dok.dateityp?.includes('image') || dok.dateityp?.includes('pdf')) {
      try {
        const token = localStorage.getItem('token');
        const response = await fetch(`${apiUrl}/dokumente/download/${dok.id}`, {
          headers: { 'Authorization': `Bearer ${token}` }
        });
        if (response.ok) {
          const blob = await response.blob();
          const url = window.URL.createObjectURL(blob);
          setPreviewBlob(blob);
          setPreviewUrl(url);
        }
      } catch (error) {
        console.error('Preview Fehler:', error);
      }
    }
  };

  // Gefilterte Dokumente
  const filteredDokumente = useMemo(() => {
    return dokumente.filter(dok => {
      // Kategorie-Filter
      if (filterKategorie && dok.kategorie !== filterKategorie) {
        return false;
      }
      // Anfrage-Filter
      if (filterAnfrage) {
        if (filterAnfrage === 'ohne' && dok.anfrageId) {
          return false;
        }
        if (filterAnfrage !== 'ohne' && dok.anfrageId !== filterAnfrage) {
          return false;
        }
      }
      // Projekt-Filter
      if (filterProjekt) {
        if (filterProjekt === 'ohne' && dok.projektId) {
          return false;
        }
        if (filterProjekt !== 'ohne' && dok.projektId !== filterProjekt) {
          return false;
        }
      }
      // Ansprechpartner-Filter
      if (filterAnsprechpartner) {
        if (filterAnsprechpartner === 'hauptlieferant') {
          // Nur Dokumente des Hauptlieferanten (kundeId === kundeId, kein hauptLieferantId)
          if (dok.kundeId !== kundeId) return false;
        } else {
          if (dok.kundeId !== filterAnsprechpartner) return false;
        }
      }
      return true;
    });
  }, [dokumente, filterKategorie, filterAnfrage, filterProjekt, filterAnsprechpartner, kundeId]);

  const hasActiveFilters = filterKategorie || filterAnfrage || filterProjekt || filterAnsprechpartner;

  const clearFilters = () => {
    setFilterKategorie('');
    setFilterAnfrage('');
    setFilterProjekt('');
    setFilterAnsprechpartner('');
  };

  useEffect(() => {
    loadData();
  }, [kundeId]);

  const loadData = async () => {
    try {
      const hasAp = ansprechpartner.length > 0;
      const [dokumenteData, rechnungenData] = await Promise.all([
        dokumentApi.getDokumenteByKunde(kundeId, { inkludiereAnsprechpartner: hasAp }),
        rechnungApi.getRechnungen({ kundeId })
      ]);
      setDokumente(dokumenteData);
      setRechnungen(rechnungenData);
      if (onDokumenteChange) {
        onDokumenteChange();
      }
    } catch (error) {
      console.error('Fehler beim Laden der Daten:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadDokumente = async () => {
    await loadData();
  };

  // Finde E-Rechnung zu einem Dokument (über Rechnungsnummer im Dateinamen)
  const findErechnung = (dok) => {
    if (dok.kategorie !== 'Rechnung') return null;
    // Dateiname: "Rechnung_RE-2025-00001.pdf" oder ähnlich
    const match = dok.originalname?.match(/RE-\d{4}-\d{5}/);
    if (!match) return null;
    const rechnungsnummer = match[0];
    return rechnungen.find(r => r.rechnungsnummer === rechnungsnummer && r.eRechnungFormat);
  };

  // Finde Rechnung zu einem Dokument (über Rechnungsnummer im Dateinamen)
  const findRechnung = (dok) => {
    if (dok.kategorie !== 'Rechnung') return null;
    const match = dok.originalname?.match(/RE-\d{4}-\d{5}/);
    if (!match) return null;
    const rechnungsnummer = match[0];
    return rechnungen.find(r => r.rechnungsnummer === rechnungsnummer);
  };

  // Mahnhistorie laden
  const loadMahnhistorie = async (rechnungId) => {
    if (mahnhistorie[rechnungId]) {
      // Toggle
      setExpandedMahnungen(prev => ({
        ...prev,
        [rechnungId]: !prev[rechnungId]
      }));
      return;
    }

    try {
      const mahnungen = await mahnungApi.getMahnungenByRechnung(rechnungId);
      setMahnhistorie(prev => ({ ...prev, [rechnungId]: mahnungen }));
      setExpandedMahnungen(prev => ({ ...prev, [rechnungId]: true }));
    } catch (error) {
      console.error('Fehler beim Laden der Mahnhistorie:', error);
    }
  };

  const handleUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    setUploading(true);
    try {
      const formData = new FormData();
      formData.append('datei', file);
      formData.append('kundeId', kundeId);

      await dokumentApi.uploadDokument(formData);
      await loadDokumente();
    } catch (error) {
      console.error('Upload Fehler:', error);
      alert('Fehler beim Hochladen: ' + (error.response?.data?.error || error.message));
    } finally {
      setUploading(false);
      e.target.value = '';
    }
  };

  // iOS Share-Dialog State
  const [showIOSShareDialog, setShowIOSShareDialog] = useState(false);
  const [iosShareData, setIosShareData] = useState(null);

  const handleDownload = async (dokument) => {
    try {
      const authToken = localStorage.getItem('token');
      const apiUrl = import.meta.env.VITE_API_URL || 'http://localhost:3000/api';

      // Für alle Geräte: Blob-basierter Download
      const response = await fetch(`${apiUrl}/dokumente/download/${dokument.id}`, {
        headers: {
          'Authorization': `Bearer ${authToken}`
        }
      });

      if (!response.ok) throw new Error('Download fehlgeschlagen');
      const blob = await response.blob();

      // iOS Safari spezielle Behandlung
      const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent);

      if (isIOS) {
        // Auf iOS: Zeige Dialog mit Share-Option
        const url = window.URL.createObjectURL(blob);
        setIosShareData({ url, blob, filename: dokument.originalname, mimeType: blob.type });
        setShowIOSShareDialog(true);
        return;
      }

      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = dokument.originalname;
      a.style.display = 'none';
      document.body.appendChild(a);

      // Für Mobile (Android): Timeout vor Klick für bessere Kompatibilität
      if (isMobile) {
        setTimeout(() => {
          a.click();
          setTimeout(() => {
            window.URL.revokeObjectURL(url);
            document.body.removeChild(a);
          }, 250);
        }, 100);
      } else {
        a.click();
        setTimeout(() => {
          window.URL.revokeObjectURL(url);
          document.body.removeChild(a);
        }, 100);
      }
    } catch (error) {
      console.error('Download Fehler:', error);
      alert('Fehler beim Download: ' + error.message);
    }
  };

  // iOS Share-Funktion
  const handleIOSShare = async () => {
    if (!iosShareData) return;

    try {
      // Versuche File-Konstruktor (funktioniert auf neueren iOS-Versionen)
      if (typeof File !== 'undefined' && navigator.share && navigator.canShare) {
        try {
          const file = new File([iosShareData.blob], iosShareData.filename, { type: iosShareData.mimeType });
          if (navigator.canShare({ files: [file] })) {
            await navigator.share({
              files: [file],
              title: iosShareData.filename
            });
            closeIOSShareDialog();
            return;
          }
        } catch (fileError) {
          console.log('File-Konstruktor nicht verfügbar:', fileError);
        }
      }
      // Fallback: Öffne in neuem Tab
      window.open(iosShareData.url, '_blank');
    } catch (error) {
      if (error.name !== 'AbortError') {
        // Fallback: Öffne in neuem Tab
        window.open(iosShareData.url, '_blank');
      }
    }
    closeIOSShareDialog();
  };

  const closeIOSShareDialog = () => {
    if (iosShareData?.url) {
      window.URL.revokeObjectURL(iosShareData.url);
    }
    setShowIOSShareDialog(false);
    setIosShareData(null);
  };

  const handlePdfOpen = async (dokument) => {
    try {
      const token = localStorage.getItem('token');
      const apiUrl = import.meta.env.VITE_API_URL || 'http://localhost:3000/api';
      const response = await fetch(`${apiUrl}/dokumente/download/${dokument.id}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (!response.ok) throw new Error('PDF konnte nicht geladen werden');
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      window.open(url, '_blank');
    } catch (error) {
      console.error('PDF Fehler:', error);
      alert('Fehler beim Öffnen der PDF');
    }
  };

  const isPdf = (dateityp) => dateityp?.includes('pdf');

  const handleEdit = (dokument) => {
    setEditingDokument(dokument);
    setEditForm({
      kategorie: dokument.kategorie,
      anfrageId: dokument.anfrageId || '',
      beschreibung: dokument.beschreibung || ''
    });
  };

  const handleSaveEdit = async () => {
    try {
      await dokumentApi.updateDokument(editingDokument.id, editForm);
      await loadDokumente();
      setEditingDokument(null);
    } catch (error) {
      console.error('Update Fehler:', error);
      alert('Fehler beim Speichern');
    }
  };

  const handleDelete = async (dokument) => {
    if (!confirm(`Dokument "${dokument.originalname}" wirklich löschen?`)) return;

    try {
      await dokumentApi.deleteDokument(dokument.id);
      await loadDokumente();
    } catch (error) {
      console.error('Löschen Fehler:', error);
      alert('Fehler beim Löschen');
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center py-8">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Upload Box - nur wenn nicht readOnly */}
      {!readOnly && (
        <label className="block border-2 border-dashed border-border rounded-lg p-6 text-center cursor-pointer hover:border-primary hover:bg-primary-light/10 transition">
          <input
            type="file"
            onChange={handleUpload}
            className="hidden"
            accept=".pdf,.doc,.docx,.xls,.xlsx,.txt,.jpg,.jpeg,.png,.gif"
            disabled={uploading}
          />
          <Upload className="w-8 h-8 mx-auto mb-2 text-text-muted" />
          {uploading ? (
            <p className="text-text-muted">Wird hochgeladen...</p>
          ) : (
            <>
              <p className="font-medium">Dokument hochladen</p>
              <p className="text-sm text-text-muted mt-1">
                PDF, Word, Excel, Text oder Bilder (max. 20MB)
              </p>
            </>
          )}
        </label>
      )}

      {/* Filter */}
      {dokumente.length > 0 && (
        <div className="flex flex-wrap items-center gap-3 p-3 bg-gray-50 rounded-lg">
          <div className="flex items-center gap-2 text-sm text-text-muted">
            <Filter className="w-4 h-4" />
            <span>Filter:</span>
          </div>

          {/* Kategorie Filter */}
          <select
            value={filterKategorie}
            onChange={(e) => setFilterKategorie(e.target.value)}
            className="px-3 py-1.5 text-sm border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary bg-white"
          >
            <option value="">Alle Kategorien</option>
            {KATEGORIEN.map((kat) => (
              <option key={kat} value={kat}>{kat}</option>
            ))}
          </select>

          {/* Anfrage Filter */}
          <select
            value={filterAnfrage}
            onChange={(e) => setFilterAnfrage(e.target.value)}
            className="px-3 py-1.5 text-sm border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary bg-white"
          >
            <option value="">Alle Anfragen</option>
            <option value="ohne">Ohne Zuordnung</option>
            {anfragen.map((anf) => (
              <option key={anf.id} value={anf.id}>{anf.betreff}</option>
            ))}
          </select>

          {/* Projekt Filter */}
          {projekte.length > 0 && (
            <select
              value={filterProjekt}
              onChange={(e) => setFilterProjekt(e.target.value)}
              className="px-3 py-1.5 text-sm border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary bg-white"
            >
              <option value="">Alle Projekte</option>
              <option value="ohne">Ohne Projekt</option>
              {projekte.map((proj) => (
                <option key={proj.id} value={proj.id}>{proj.nummer} - {proj.titel}</option>
              ))}
            </select>
          )}

          {/* Ansprechpartner Filter */}
          {ansprechpartner.length > 0 && (
            <select
              value={filterAnsprechpartner}
              onChange={(e) => setFilterAnsprechpartner(e.target.value)}
              className="px-3 py-1.5 text-sm border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary bg-white"
            >
              <option value="">Alle Ansprechpartner</option>
              <option value="hauptlieferant">Nur Hauptlieferant</option>
              {ansprechpartner.map((ap) => (
                <option key={ap.id} value={ap.id}>{ap.name}</option>
              ))}
            </select>
          )}

          {/* Filter zurücksetzen */}
          {hasActiveFilters && (
            <button
              onClick={clearFilters}
              className="px-3 py-1.5 text-sm text-primary hover:bg-primary-light rounded-lg transition"
            >
              Zurücksetzen
            </button>
          )}

          {/* Ergebnis-Anzeige */}
          <span className="ml-auto text-sm text-text-muted">
            {filteredDokumente.length} von {dokumente.length} Dokumenten
          </span>
        </div>
      )}

      {/* Dokumente Liste */}
      {dokumente.length === 0 ? (
        <div className="text-center py-8 text-text-muted">
          <FileText className="w-12 h-12 mx-auto mb-2 opacity-50" />
          <p>Noch keine Dokumente vorhanden</p>
        </div>
      ) : filteredDokumente.length === 0 ? (
        <div className="text-center py-8 text-text-muted">
          <Filter className="w-12 h-12 mx-auto mb-2 opacity-50" />
          <p>Keine Dokumente gefunden</p>
          <button
            onClick={clearFilters}
            className="mt-2 text-primary hover:underline text-sm"
          >
            Filter zurücksetzen
          </button>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {filteredDokumente.map((dok) => {
            const fileStyle = getFileIcon(dok.dateityp);
            const IconComponent = fileStyle.icon;
            const dokIsPdf = isPdf(dok.dateityp);
            const eRechnung = findErechnung(dok);

            return (
              <div
                key={dok.id}
                className="bg-white border border-border rounded-lg p-4 hover:shadow-md transition"
                onMouseEnter={(e) => !isMobile && handleDocMouseEnter(e, dok)}
                onMouseLeave={() => !isMobile && handleDocMouseLeave()}
                onClick={(e) => handleDocClick(e, dok)}
              >
                <div className="flex items-start gap-3">
                  {/* Icon - klickbar für PDFs */}
                  {dokIsPdf ? (
                    <button
                      onClick={() => handlePdfOpen(dok)}
                      className={`p-3 rounded-lg ${fileStyle.bg} hover:opacity-80 transition cursor-pointer`}
                      title="PDF im Browser öffnen"
                    >
                      <IconComponent className={`w-6 h-6 ${fileStyle.color}`} />
                    </button>
                  ) : (
                    <div className={`p-3 rounded-lg ${fileStyle.bg}`}>
                      <IconComponent className={`w-6 h-6 ${fileStyle.color}`} />
                    </div>
                  )}

                  {/* Info */}
                  <div className="flex-1 min-w-0">
                    <h4 className="font-medium text-sm truncate" title={dok.originalname}>
                      {dok.originalname}
                    </h4>
                    <p className="text-xs text-text-muted mt-1">
                      {formatFileSize(dok.groesse)} • {new Date(dok.erstelltAm).toLocaleDateString('de-DE')}
                    </p>

                    {/* Tags */}
                    <div className="flex flex-wrap gap-2 mt-2">
                      <span className={`text-xs px-2 py-0.5 rounded-full ${getKategorieBadge(dok.kategorie)}`}>
                        {dok.kategorie}
                      </span>
                      {dok.anfrage && (
                        <span className="text-xs px-2 py-0.5 rounded-full bg-yellow-100 text-yellow-700">
                          {dok.anfrage.betreff}
                        </span>
                      )}
                      {dok.projekt && (
                        <span className="text-xs px-2 py-0.5 rounded-full bg-indigo-100 text-indigo-700">
                          {dok.projekt.nummer}
                        </span>
                      )}
                      {ansprechpartner.length > 0 && dok.kunde?.hauptLieferantId && (
                        <span className="text-xs px-2 py-0.5 rounded-full bg-amber-100 text-amber-700">
                          {dok.kunde.name}
                        </span>
                      )}
                    </div>

                    {/* E-Rechnung Badge */}
                    {eRechnung && (
                      <div className="flex items-center gap-1 mt-2">
                        <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-xs font-medium bg-green-100 text-green-700" title="ZUGFeRD 2.1 E-Rechnung - XML in PDF eingebettet">
                          <FileCheck className="w-3 h-3" />
                          E-Rechnung
                        </span>
                      </div>
                    )}

                    {/* Mahnungs-Badge und Historie */}
                    {(() => {
                      const rechnung = findRechnung(dok);
                      if (!rechnung || !rechnung.anzahlMahnungen) return null;

                      return (
                        <div className="mt-2">
                          <button
                            onClick={() => loadMahnhistorie(rechnung.id)}
                            className="inline-flex items-center gap-1 text-xs text-orange-600 hover:text-orange-800 transition"
                          >
                            <AlertTriangle className="w-3 h-3" />
                            {rechnung.anzahlMahnungen}x gemahnt
                            {expandedMahnungen[rechnung.id] ? (
                              <ChevronUp className="w-3 h-3" />
                            ) : (
                              <ChevronDown className="w-3 h-3" />
                            )}
                          </button>

                          {/* Mahnhistorie Dropdown */}
                          {expandedMahnungen[rechnung.id] && mahnhistorie[rechnung.id] && (
                            <div className="mt-2 pl-3 border-l-2 border-orange-200 space-y-1">
                              {mahnhistorie[rechnung.id].map((mahnung) => (
                                <div key={mahnung.id} className="text-xs">
                                  <div className="flex items-center gap-2">
                                    <span className="font-medium text-gray-700">
                                      {mahnung.mahnstufe}. Mahnung
                                    </span>
                                    <span className="text-gray-500">
                                      {new Date(mahnung.versendetAm).toLocaleDateString('de-DE', {
                                        day: '2-digit',
                                        month: '2-digit',
                                        year: 'numeric',
                                        hour: '2-digit',
                                        minute: '2-digit'
                                      })}
                                    </span>
                                  </div>
                                  {mahnung.emailVersendet && (
                                    <span className="text-green-600 text-xs">E-Mail versendet</span>
                                  )}
                                </div>
                              ))}
                            </div>
                          )}
                        </div>
                      );
                    })()}

                    {dok.beschreibung && (
                      <p className="text-xs text-text-muted mt-2 line-clamp-2">
                        {dok.beschreibung}
                      </p>
                    )}
                  </div>

                  {/* Actions */}
                  <div className="flex gap-1">
                    <button
                      onClick={() => handleDownload(dok)}
                      className="p-2 hover:bg-gray-100 rounded-lg transition"
                      title="Herunterladen"
                    >
                      <Download className="w-4 h-4 text-text-muted" />
                    </button>
                    {!readOnly && (
                      <>
                        <button
                          onClick={() => handleEdit(dok)}
                          className="p-2 hover:bg-gray-100 rounded-lg transition"
                          title="Bearbeiten"
                        >
                          <Pencil className="w-4 h-4 text-text-muted" />
                        </button>
                        <button
                          onClick={() => handleDelete(dok)}
                          className="p-2 hover:bg-red-50 rounded-lg transition"
                          title="Löschen"
                        >
                          <Trash2 className="w-4 h-4 text-danger" />
                        </button>
                      </>
                    )}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Edit Modal */}
      {editingDokument && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-white rounded-card shadow-lg p-6 w-full max-w-md mx-4">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold">Dokument bearbeiten</h3>
              <button
                onClick={() => setEditingDokument(null)}
                className="p-1 hover:bg-gray-100 rounded"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            <div className="space-y-4">
              {/* Dateiname (nur anzeigen) */}
              <div>
                <label className="block text-sm font-medium mb-1">Datei</label>
                <p className="text-sm text-text-muted truncate">
                  {editingDokument.originalname}
                </p>
              </div>

              {/* Kategorie */}
              <div>
                <label className="block text-sm font-medium mb-1">Kategorie</label>
                <select
                  value={editForm.kategorie}
                  onChange={(e) => setEditForm({ ...editForm, kategorie: e.target.value })}
                  className="w-full px-3 py-2 border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary"
                >
                  {KATEGORIEN.map((kat) => (
                    <option key={kat} value={kat}>{kat}</option>
                  ))}
                </select>
              </div>

              {/* Anfrage zuordnen */}
              <div>
                <label className="block text-sm font-medium mb-1">Anfrage zuordnen</label>
                <select
                  value={editForm.anfrageId}
                  onChange={(e) => setEditForm({ ...editForm, anfrageId: e.target.value })}
                  className="w-full px-3 py-2 border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary"
                >
                  <option value="">Keine Zuordnung</option>
                  {anfragen.map((anf) => (
                    <option key={anf.id} value={anf.id}>{anf.betreff}</option>
                  ))}
                </select>
              </div>

              {/* Beschreibung */}
              <div>
                <label className="block text-sm font-medium mb-1">Beschreibung</label>
                <textarea
                  value={editForm.beschreibung}
                  onChange={(e) => setEditForm({ ...editForm, beschreibung: e.target.value })}
                  className="w-full px-3 py-2 border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary"
                  rows={3}
                  placeholder="Optionale Beschreibung..."
                />
              </div>
            </div>

            <div className="flex gap-3 mt-6">
              <button
                onClick={() => setEditingDokument(null)}
                className="flex-1 px-4 py-2 border border-border rounded-button hover:bg-gray-50 transition"
              >
                Abbrechen
              </button>
              <button
                onClick={handleSaveEdit}
                className="flex-1 px-4 py-2 bg-primary text-white rounded-button hover:bg-primary-dark transition"
              >
                Speichern
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Dokument-Preview Popup */}
      {previewDokument && (
        <>
          {/* Backdrop für Mobile - Tippen schließt Preview */}
          {isMobile && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.2 }}
              className="fixed inset-0 bg-black/40 z-40"
              onClick={closePreview}
            />
          )}
          <motion.div
            initial={isMobile ? { opacity: 0, y: 100 } : { opacity: 0, scale: 0.9 }}
            animate={isMobile ? { opacity: 1, y: 0 } : { opacity: 1, scale: 1 }}
            exit={isMobile ? { opacity: 0, y: 100 } : { opacity: 0, scale: 0.95 }}
            transition={{ duration: 0.25, ease: [0.25, 0.1, 0.25, 1] }}
            className={`fixed z-50 bg-white shadow-2xl border border-gray-200 overflow-hidden ${
              isMobile
                ? 'left-4 right-4 rounded-2xl'
                : 'rounded-xl'
            }`}
            style={isMobile ? {
              bottom: '140px',
              maxHeight: 'calc(100vh - 200px)'
            } : {
              top: previewPosition.top,
              left: previewPosition.left,
              width: '420px',
              maxWidth: 'calc(100vw - 16px)'
            }}
            onMouseEnter={!isMobile ? handlePreviewMouseEnter : undefined}
            onMouseLeave={!isMobile ? handlePreviewMouseLeave : undefined}
          >
            {/* Schließen-Button für Mobile */}
            {isMobile && (
              <button
                onClick={closePreview}
                className="absolute top-3 right-3 z-10 p-2 bg-black/10 hover:bg-black/20 rounded-full transition"
                aria-label="Vorschau schließen"
              >
                <X className="w-5 h-5 text-gray-700" />
              </button>
            )}

            {/* Bild-Vorschau */}
          {previewDokument.dateityp?.includes('image') && previewUrl && (
            <div className="relative bg-gray-100">
              <img
                src={previewUrl}
                alt={previewDokument.originalname}
                className="w-full object-contain"
                style={{ height: '280px' }}
              />
            </div>
          )}

          {/* PDF-Vorschau */}
          {previewDokument.dateityp?.includes('pdf') && (
            <div className="relative bg-gray-100" style={{ height: '420px' }}>
              {previewUrl ? (
                <iframe
                  src={`${previewUrl}#toolbar=0&navpanes=0&scrollbar=0&view=FitH`}
                  title="PDF Preview"
                  className="w-full h-full border-0"
                />
              ) : (
                <div className="flex items-center justify-center h-full">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
                </div>
              )}
            </div>
          )}

          {/* Word-Vorschau */}
          {(previewDokument.dateityp?.includes('word') || previewDokument.dateityp?.includes('document')) && (
            <div className="relative bg-blue-50 h-32 flex items-center justify-center">
              <div className="text-center">
                <FileText className="w-12 h-12 text-blue-500 mx-auto mb-2" />
                <p className="text-sm text-blue-600 font-medium">Word-Dokument</p>
              </div>
            </div>
          )}

          {/* Excel-Vorschau */}
          {(previewDokument.dateityp?.includes('excel') || previewDokument.dateityp?.includes('spreadsheet')) && (
            <div className="relative bg-green-50 h-32 flex items-center justify-center">
              <div className="text-center">
                <FileSpreadsheet className="w-12 h-12 text-green-500 mx-auto mb-2" />
                <p className="text-sm text-green-600 font-medium">Excel-Dokument</p>
              </div>
            </div>
          )}

          {/* Info-Bereich */}
          <div className="p-3 bg-white border-t border-gray-100">
            <p className="text-sm font-medium text-gray-900 truncate">{previewDokument.originalname}</p>
            <div className="flex items-center justify-between mt-2">
              <span className="text-xs text-gray-500">{formatFileSize(previewDokument.groesse)}</span>
              <div className="flex gap-1">
                {isPdf(previewDokument.dateityp) && (
                  isMobile && previewUrl ? (
                    <a
                      href={previewUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      onClick={(e) => {
                        e.stopPropagation();
                        closePreview();
                      }}
                      className="flex items-center gap-1 px-3 py-2 text-sm bg-primary text-white rounded-lg hover:bg-primary-dark transition"
                    >
                      <Eye className="w-4 h-4" />
                      Öffnen
                    </a>
                  ) : (
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        const dok = previewDokument;
                        if (isMobile) closePreview();
                        handlePdfOpen(dok);
                      }}
                      className="flex items-center gap-1 px-3 py-2 text-sm bg-primary text-white rounded-lg hover:bg-primary-dark transition"
                    >
                      <Eye className="w-4 h-4" />
                      Öffnen
                    </button>
                  )
                )}
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    const dok = previewDokument;
                    if (isMobile) closePreview();
                    handleDownload(dok);
                  }}
                  className="flex items-center gap-1 px-3 py-2 text-sm bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition"
                >
                  <Download className="w-4 h-4" />
                  Download
                </button>
              </div>
            </div>
          </div>
          </motion.div>
        </>
      )}

      {/* iOS Share Dialog */}
      {showIOSShareDialog && (
        <>
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/50 z-50"
            onClick={closeIOSShareDialog}
          />
          <motion.div
            initial={{ opacity: 0, y: 100 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: 100 }}
            className="fixed left-4 right-4 bottom-8 z-50 bg-white rounded-2xl shadow-2xl overflow-hidden"
          >
            <div className="p-5">
              <div className="flex items-center gap-3 mb-4">
                <div className="p-3 bg-primary-light rounded-xl">
                  <Download className="w-6 h-6 text-primary" />
                </div>
                <div className="flex-1 min-w-0">
                  <h3 className="font-semibold text-gray-900">Datei speichern</h3>
                  <p className="text-sm text-gray-500 truncate">{iosShareData?.filename}</p>
                </div>
              </div>

              <div className="space-y-3">
                {/* Share-Button (öffnet natives iOS Share-Menü) */}
                <button
                  onClick={handleIOSShare}
                  className="w-full flex items-center justify-center gap-2 bg-primary text-white py-3.5 rounded-xl font-medium hover:bg-primary-dark transition"
                >
                  <Share2 className="w-5 h-5" />
                  Teilen / In Dateien sichern
                </button>

                {/* Öffnen-Button */}
                <button
                  onClick={() => {
                    if (iosShareData?.url) {
                      window.open(iosShareData.url, '_blank');
                    }
                    closeIOSShareDialog();
                  }}
                  className="w-full flex items-center justify-center gap-2 bg-gray-100 text-gray-700 py-3.5 rounded-xl font-medium hover:bg-gray-200 transition"
                >
                  <Eye className="w-5 h-5" />
                  Vorschau öffnen
                </button>

                {/* Abbrechen */}
                <button
                  onClick={closeIOSShareDialog}
                  className="w-full text-gray-500 py-2 text-sm hover:text-gray-700 transition"
                >
                  Abbrechen
                </button>
              </div>
            </div>
          </motion.div>
        </>
      )}
    </div>
  );
}

export default DokumenteTab;
